import React, { FC, useEffect, useRef, useMemo } from 'react'
import { set } from 'lodash'
import { I18n, Translate } from 'react-redux-i18n'
import { useSelector } from 'react-redux'
import { Modal, ModalHeader, ModalBody } from 'reactstrap'
import Refinitiv from '../lib/refinitiv'
import { RootState } from '../types'

const refinitiv = Refinitiv.Instance

interface Props {
  symbol: string
  onClose: () => void
}

const getTranslations = (keys: Array<string>): Record<string, unknown> => {
  return keys.reduce((translations, translationKey) => {
    set(translations, translationKey.replace(/_/g, '.'),
      translationKey !== 'numberFormats_compact'
        ? I18n.t(`refinitiv_${translationKey}`)
        : I18n.t(`refinitiv_${translationKey}`)?.split(',')
    )

    return translations
  }, {})
}

const AnalysisTrendsModalDialog: FC<Props> = ({ symbol, onClose }) => {
  const estimateRatiosWidgetRef = useRef<HTMLDivElement | null>(null)
  const companyProfileWidgetRef = useRef<HTMLDivElement | null>(null)
  const theme = useSelector((state: RootState) => state.appState.appTheme)
  const locale = useSelector((state: RootState) => state.i18n.locale)

  const estimateRatiosTranslations = useMemo(() => getTranslations([
    'analystConsensus_title', 'analystConsensus_strongsell', 'analystConsensus_sell', 'analystConsensus_hold',
    'analystConsensus_buy', 'analystConsensus_strongbuy', 'analystConsensus_targetTitle', 'analystConsensus_low',
    'analystConsensus_high', 'analystConsensus_price', 'analystConsensus_tp', 'analystConsensus_recommendation',
    'analystConsensus_tpfull', 'analystConsensus_pltg', 'titles_companyInfo', 'titles_total',
    'titles_valuationProfit', 'titles_forecast', 'titles_current', 'titles_perShareData', 'table_marketCap',
    'table_sharesOutstanding', 'table_commonshare', 'table_enterpriseValue', 'table_numberEmployees',
    'table_pe', 'table_ps', 'table_pb', 'table_pcf', 'table_peg', 'table_earningYield', 'table_dividendYield',
    'table_revenue', 'table_ebit', 'table_netIncome', 'table_gpm', 'table_roe', 'table_eps', 'table_rps',
    'table_bps', 'table_cfps', 'table_cps', 'table_dividendPerShare', 'table_captions_companyInfo',
    'table_captions_valuationProfit', 'table_captions_perShareData', 'table_captions_total', 'messages_nodata',
    'messages_error', 'numberFormats_compact',
  ]), [])

  const companyProfileTranslations = useMemo(() => getTranslations([
    'titles_masterData', 'titles_clearingCodes', 'titles_keyDates', 'titles_activities',
    'titles_management', 'table_symbol', 'table_name', 'table_companyID', 'table_price',
    'table_currency', 'table_exchange', 'table_parValue', 'table_sharesOutstanding',
    'table_marketCap', 'table_freeFloat', 'table_employees', 'table_headquarters', 'table_phone',
    'table_fax', 'table_internet', 'table_isin', 'table_valor', 'table_cusip', 'table_finYearEnd',
    'table_reportingDateQ3', 'table_reportingDateQ4', 'table_reportingDateQ1',
    'table_reportingDateQ2', 'table_reportingDateA', 'table_captions_masterData',
    'table_captions_clearingCodes', 'table_captions_keyDates', 'table_captions_activities',
    'table_captions_management', 'messages_nodata', 'messages_error', 'a11y_controlBar',
  ]), [])

  useEffect(() => {
    if (estimateRatiosWidgetRef.current) {
      refinitiv.initEstimatesRatiosWidget(
          '.estimates-ratios-widget',
          {
            ric: symbol
          },
          locale,
        estimateRatiosTranslations,
          theme === 'Dark' ? 'refinitiv-dark' : 'refinitiv-light'
        )
        .catch((err) => console.warn(err.message))
    }
  }, [symbol, locale, theme, estimateRatiosTranslations])

  useEffect(() => {
    if (companyProfileWidgetRef.current) {
      refinitiv
        .initCompanyProfileWidget(
          '.company-profile-widget',
          {
            ric: symbol
          },
          locale,
          companyProfileTranslations,
          theme === 'Dark' ? 'refinitiv-dark' : 'refinitiv-light'
        )
        .catch(err => console.warn(err.message))
    }
  }, [symbol, locale, theme, companyProfileTranslations])

  return (
    <Modal
      modalClassName={'analysis-trends-modal'}
      className={'analysis-trends-modal-dialog'}
      isOpen={!!symbol}
      toggle={onClose}
      backdrop={true}
      autoFocus={false}
    >
      <ModalHeader toggle={onClose}>
        <Translate value={'fundamentalAnalysis'} />
      </ModalHeader>
      <ModalBody>
        <div ref={estimateRatiosWidgetRef} className="estimates-ratios-widget" />
        <div ref={companyProfileWidgetRef} className="company-profile-widget" />
      </ModalBody>
    </Modal>
  )
}

export default AnalysisTrendsModalDialog
